import React, { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { EmbedType, MeetingSummaryElement } from '../models'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import {
  selectIsAccumulatingMeetingSummary,
  selectMeetingSummaryAgentEditedLexicalState,
  selectMeetingSummaryAgentEditing,
  selectMeetingSummaryAgentElements,
  setMeetingSummaryEditedContent,
} from '../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { ReadOnlyLexicalEditor } from '../../MeetingResults/Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import { $getRoot, $insertNodes, EditorState, LexicalEditor, LexicalNode } from 'lexical'
import { $generateNodesFromDOM } from '@lexical/html'
import { $createEmbeddedMeetingInfoNode } from './Components/LexicalNodes/EmbeddedMeetingInfoNode'
import { RichTextToolbarPlugin } from '../../MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { WritableLexicalEditor } from '../../MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { createHeadlessEditor } from '@lexical/headless'
import { $createEmbeddedParticipantsNode } from './Components/LexicalNodes/EmbeddedParticipantsNode'
import { $createEmbeddedMeetingResultNode } from './Components/LexicalNodes/EmbeddedMeetingResultNode'
import { MEETING_SUMMARY_NODES } from './Components/LexicalNodes/utils'
import { PreventDecoratorNodeDeletePlugin } from '../../MeetingResults/Lexical/Plugins/PreventDecoratorNodeDeletePlugin/PreventDecoratorNodeDeletePlugin'
import { ManuallyExecuteMeetingSummary } from '../MeetingSummaryAgent'
import './MeetingSummaryAgentContent.scss'
import { parseMeetingSummary } from '../utils'
import { getHtmlContentFromState } from '../../MeetingResults/Lexical/utils'
import { MeetingSummaryAgentLoader } from '../Loader/MeetingSummaryAgentLoader'

const getLexicalNodesForMeetingSummaryElement = (editor: LexicalEditor, element: MeetingSummaryElement): LexicalNode[] => {
  const { type } = element
  const parser = new DOMParser()
  switch (type) {
    case 'text':
      return $generateNodesFromDOM(editor, parser.parseFromString(element.text, 'text/html'))
    case 'embed':
      switch (element.embedType) {
        case EmbedType.MEETING_INFO:
          return [$createEmbeddedMeetingInfoNode(element)]
        case EmbedType.PARTICIPANTS:
          return [$createEmbeddedParticipantsNode(element)]
        case EmbedType.MEETING_RESULT:
          return [$createEmbeddedMeetingResultNode(element)]
      }
  }
  return []
}

const getEditorStateFromElements = (elements: MeetingSummaryElement[]) => {
  const editor = createHeadlessEditor({ nodes: MEETING_SUMMARY_NODES })
  editor.update(
    () => {
      const lexicalNodes = elements.map((element) => getLexicalNodesForMeetingSummaryElement(editor, element)).flat()
      $getRoot().select()
      $insertNodes(lexicalNodes)
    },
    { discrete: true },
  )
  return editor.getEditorState()
}

const MEETING_SUMMARY_LOADING_TEXT = 'Updating summary…'

export const MeetingSummaryAgentContent: React.FC = () => {
  const dispatch = useAppDispatch()
  const elements = useAppSelector(selectMeetingSummaryAgentElements)
  const editing = useAppSelector(selectMeetingSummaryAgentEditing)
  const isAccumulatingMeetingSummary = useAppSelector(selectIsAccumulatingMeetingSummary)

  const headerRef = useRef<HTMLDivElement | null>(null)
  const editorRef = useRef<LexicalEditor>(null)

  const [stickied, setStickied] = useState<boolean>(false)

  const handleOnScroll = () => {
    if (!headerRef.current) {
      return
    }
    const headerOffset = 79
    if (headerRef.current.getBoundingClientRect().top - headerOffset <= 0) {
      setStickied(true)
    } else {
      setStickied(false)
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll)
    return () => window.removeEventListener('scroll', handleOnScroll)
  }, [])

  const initialEditorState = useMemo<EditorState>(() => getEditorStateFromElements(elements), [elements])
  const lastLexicalState = useAppSelector(selectMeetingSummaryAgentEditedLexicalState)

  useEffect(() => {
    setTimeout(() => editorRef.current?.setEditorState(initialEditorState))
  }, [initialEditorState])

  const handleChange = (editorState: EditorState) => {
    const lexicalState = JSON.stringify(editorState)
    if (lastLexicalState !== lexicalState) {
      dispatch(
        setMeetingSummaryEditedContent({
          editedLexicalState: lexicalState,
          editedElements: parseMeetingSummary(getHtmlContentFromState(lexicalState, MEETING_SUMMARY_NODES)),
        }),
      )
    }
  }
  if (isAccumulatingMeetingSummary) {
    return <MeetingSummaryAgentLoader loadingText={MEETING_SUMMARY_LOADING_TEXT} />
  }

  if (editing) {
    return (
      <WritableLexicalEditor
        editorContainerClassName='flex-grow-1'
        onChange={handleChange}
        initialEditorState={initialEditorState}
        lexicalNodes={MEETING_SUMMARY_NODES}
        errorFallback={<ManuallyExecuteMeetingSummary />}
        header={
          <div
            ref={headerRef}
            className={classNames('meeting-summary-agent-rich-text-header', {
              'meeting-summary-agent-rich-text-header--stickied bg-white zocks-px-0.75': stickied,
            })}
          >
            <RichTextToolbarPlugin className='px-1 zocks-py-0.75 gap-2 '>
              <div className='editable-text-result-rich-text-divider mb-0' />
            </RichTextToolbarPlugin>
          </div>
        }
      >
        <PreventDecoratorNodeDeletePlugin />
      </WritableLexicalEditor>
    )
  }

  return (
    <ReadOnlyLexicalEditor
      ref={editorRef}
      initialEditorState={initialEditorState}
      onChange={handleChange}
      lexicalNodes={MEETING_SUMMARY_NODES}
      errorFallback={<ManuallyExecuteMeetingSummary />}
    />
  )
}
